import { useTeamQuotaUsage } from 'features/team-usage/hooks/use-team-quota-usage';
import { toHardQuotas } from 'features/team-usage/quota-usage';
import { useAuth } from 'lib/auth';
import { useLocation, useNavigate } from 'react-router';
import { routes } from 'routes/routes';

import {
  type ActiveSettingsItem,
  SETTINGS_ACTIVE_ROUTES,
  SettingsSidebar,
} from './settings-sidebar';
import { useSidebarUI } from './sidebar-context';
import { useAskAi } from './use-ask-ai';

const useActiveSettingsItem = (): ActiveSettingsItem | null => {
  const { pathname } = useLocation();

  return (
    SETTINGS_ACTIVE_ROUTES.find((key) => {
      const path = routes[key].build();
      return pathname === path || pathname.startsWith(`${path}/`);
    }) ?? null
  );
};

interface SettingsSidebarContainerProps {
  /** When rendered inside the mobile drawer, always show the full sidebar. */
  inDrawer?: boolean;
  /** Drawer override: swap the drawer view instead of navigating back. */
  onBack?: () => void;
}

export const SettingsSidebarContainer = ({
  inDrawer = false,
  onBack,
}: SettingsSidebarContainerProps) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const activeItem = useActiveSettingsItem();
  const onAskAi = useAskAi();
  const { toggle, isDesktop, isCollapsed } = useSidebarUI();
  const { quotas } = useTeamQuotaUsage();

  const userEmail = auth.user?.user.email ?? '';
  const firstName = auth.user?.user.first_name ?? '';
  const lastName = auth.user?.user.last_name ?? '';
  const userName = [firstName, lastName].filter(Boolean).join(' ');

  const handleBack = () => {
    const projectId = auth.project.selected?.id;
    if (projectId) {
      navigate(routes.dashboard.build({ project: projectId }));
    } else {
      navigate(routes.settingsProjects.build());
    }
  };

  return (
    <SettingsSidebar
      collapsed={inDrawer ? false : !isDesktop || isCollapsed}
      userName={userName || userEmail}
      userEmail={userEmail}
      permissions={auth.user?.user.team.permissions.all ?? []}
      hasHardLimits={quotas ? toHardQuotas(quotas).length > 0 : undefined}
      activeItem={activeItem}
      isSigningOut={auth.logout.isPending}
      onBack={onBack ?? handleBack}
      onToggle={toggle}
      onSignOut={() => auth.logout.mutate()}
      onAskAi={onAskAi}
    />
  );
};
SettingsSidebarContainer.displayName = 'SettingsSidebarContainer';
